/**
 * Price-based cart limit – counts paid (non-sample) products above a price threshold.
 * Sample limit logic lives separately in sticky-cart-limit-bar.js and filter-card-product.liquid.
 */
(function () {
  'use strict';

  function getSettings() {
    var settings = window.cartPriceLimitSettings || {};
    return {
      enabled: settings.enabled !== false,
      maxLimit: parseInt(settings.maxLimit, 10) || 20,
      minPriceCents: parseInt(settings.minPriceCents, 10) || 0,
      showRemaining: settings.showRemaining !== false,
      limitReachedMessage: settings.limitReachedMessage || '',
    };
  }

  function isSampleCartItem(item) {
    if (!item) return false;
    var props = item.properties || {};
    return props._is_sample == true || props._is_sample == 'true';
  }

  function getItemPriceCents(item) {
    if (!item) return 0;
    if (item.final_price != null) return parseInt(item.final_price, 10) || 0;
    if (item.price != null) return parseInt(item.price, 10) || 0;
    return 0;
  }

  function isPriceBasedCartItem(item, minPriceCents) {
    if (!item || isSampleCartItem(item)) return false;
    var price = getItemPriceCents(item);
    return price > minPriceCents;
  }

  function isPriceBasedVariantPrice(priceCents, minPriceCents) {
    var price = parseInt(priceCents, 10) || 0;
    return price > minPriceCents;
  }

  function countPriceBasedQuantityInCart(cart, minPriceCents) {
    if (!cart || !cart.items || !cart.items.length) return 0;
    return cart.items.reduce(function (sum, item) {
      return sum + (isPriceBasedCartItem(item, minPriceCents) ? item.quantity || 0 : 0);
    }, 0);
  }

  function getBar() {
    return document.querySelector('[id^="sticky-cart-limit-bar-"]');
  }

  function isProductPage() {
    return document.body.classList.contains('template-product');
  }

  function isPriceLimitBar(bar) {
    return bar && bar.dataset.limitContext === 'price';
  }

  function variantQualifiesForPriceLimit(variantId, settings) {
    var variantPrice = getVariantPrice(variantId);
    if (variantPrice != null) {
      return isPriceBasedVariantPrice(variantPrice, settings.minPriceCents);
    }
    return isProductPage();
  }

  function setButtonPriceLimitDisabled(button, disabled) {
    if (!button) return;

    if (disabled) {
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-disabled', 'true');
      button.dataset.priceLimitDisabled = 'true';
      return;
    }

    if (button.dataset.priceLimitDisabled !== 'true') return;

    delete button.dataset.priceLimitDisabled;

    var form = button.closest('form');
    var variantInput = form && form.querySelector('[name="id"]');
    if (variantInput && variantInput.disabled) return;

    var requiresConfig =
      button.getAttribute('data-cf-requires-config') === '1' ||
      (button.closest && button.closest('[data-cf-requires-config="1"]'));
    var cfReady = document.documentElement.getAttribute('data-custom-fields-ready') === '1';
    if (requiresConfig && !cfReady) {
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-disabled', 'true');
      return;
    }

    button.removeAttribute('disabled');
    button.removeAttribute('aria-disabled');
  }

  function getPriceBarMinCount(bar, maxLimit) {
    if (!bar) return 0;
    var minFromData = parseInt(bar.dataset.priceShowWhenCountAtLeast, 10);
    if (!isNaN(minFromData)) return minFromData;
    var max = maxLimit != null ? maxLimit : parseInt(bar.dataset.priceMaxLimit, 10) || 20;
    return Math.max(0, max - 5);
  }

  function updatePriceBarVisibility(bar, priceCount, maxLimit) {
    if (!bar || !isPriceLimitBar(bar)) return;
    var minCount = getPriceBarMinCount(bar, maxLimit);
    var show = priceCount >= minCount;
    bar.classList.toggle('sticky-cart-limit-bar--price-visible', show);
  }

  function updatePriceBar(bar, priceCount, maxLimit) {
    if (!bar || !isPriceLimitBar(bar)) return;
    var maxEl = bar.querySelector('[data-price-cart-limit-max]');
    var currentEl = bar.querySelector('[data-price-cart-limit-current]');
    var remainingEl = bar.querySelector('[data-price-cart-limit-remaining-text]');
    var limit = maxLimit != null ? maxLimit : parseInt(bar.dataset.priceMaxLimit, 10) || 20;
    var remaining = Math.max(0, limit - priceCount);

    bar.dataset.priceCartCurrent = String(priceCount);
    bar.dataset.priceCartRemaining = String(remaining);

    if (maxEl) maxEl.textContent = limit;
    if (currentEl) currentEl.textContent = priceCount;
    if (remainingEl) {
      var template = remainingEl.getAttribute('data-remaining-template');
      if (template) {
        remainingEl.textContent = template.replace('[count]', remaining);
      } else {
        remainingEl.textContent = remainingEl.textContent.replace(/\d+/, remaining);
      }
    }

    updatePriceBarVisibility(bar, priceCount, limit);
  }

  function applyCartToPriceBar(bar, cart) {
    if (!bar || !cart || !isPriceLimitBar(bar)) return;
    var settings = getSettings();
    if (!settings.enabled) return;
    var maxLimit = parseInt(bar.dataset.priceMaxLimit, 10) || settings.maxLimit;
    var minPriceCents = parseInt(bar.dataset.priceMinPrice, 10);
    if (isNaN(minPriceCents)) minPriceCents = settings.minPriceCents;
    var priceCount = countPriceBasedQuantityInCart(cart, minPriceCents);
    updatePriceBar(bar, priceCount, maxLimit);
  }

  function refreshPriceBarFromCart(bar) {
    if (!bar) return Promise.resolve();
    return fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        applyCartToPriceBar(bar, cart);
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  function showLimitMessage() {
    var bar = getBar();
    if (!isPriceLimitBar(bar)) return;
    var settings = getSettings();
    var msgEl = bar.querySelector('.sticky-price-limit-message');
    if (!msgEl) return;
    msgEl.textContent = settings.limitReachedMessage || '';
    if (msgEl.textContent) msgEl.classList.add('is-visible');
  }

  function hideLimitMessage() {
    var bar = getBar();
    if (!isPriceLimitBar(bar)) return;
    var msgEl = bar.querySelector('.sticky-price-limit-message');
    if (!msgEl) return;
    msgEl.textContent = '';
    msgEl.classList.remove('is-visible');
  }

  function getVariantPrice(variantId) {
    if (!variantId || !window.__productVariantPrices) return null;
    var price = window.__productVariantPrices[String(variantId)];
    return price != null ? parseInt(price, 10) : null;
  }

  function resolveCartSnapshot() {
    if (window.__priceBasedCartSnapshot) {
      return Promise.resolve(window.__priceBasedCartSnapshot);
    }
    return fetch('/cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        window.__priceBasedCartSnapshot = cart;
        return cart;
      });
  }

  function wouldExceedLimit(cart, quantityToAdd, variantPriceCents) {
    var settings = getSettings();
    if (!settings.enabled) return false;
    if (!isPriceBasedVariantPrice(variantPriceCents, settings.minPriceCents)) return false;

    var currentCount = countPriceBasedQuantityInCart(cart, settings.minPriceCents);
    var addQty = parseInt(quantityToAdd, 10) || 1;
    return currentCount + addQty > settings.maxLimit;
  }

  function checkBeforeAdd(variantId, quantity, variantPriceCents) {
    var settings = getSettings();
    if (!settings.enabled) {
      return Promise.resolve({ blocked: false });
    }

    if (!isPriceBasedVariantPrice(variantPriceCents, settings.minPriceCents)) {
      return Promise.resolve({ blocked: false });
    }

    return resolveCartSnapshot().then(function (cart) {
      var blocked = wouldExceedLimit(cart, quantity, variantPriceCents);
      return { blocked: blocked, cart: cart };
    });
  }

  function validateQuantityChange(event, index, inputValue) {
    var settings = getSettings();
    if (!settings.enabled) return Promise.resolve('');

    return resolveCartSnapshot().then(function (cart) {
      if (!cart || !cart.items || !cart.items[index - 1]) return '';

      var lineItem = cart.items[index - 1];
      if (!isPriceBasedCartItem(lineItem, settings.minPriceCents)) return '';

      var previousQty = lineItem.quantity || 0;
      if (inputValue <= previousQty) return '';

      var currentCount = countPriceBasedQuantityInCart(cart, settings.minPriceCents);
      var newTotal = currentCount - previousQty + inputValue;
      if (newTotal > settings.maxLimit) {
        return settings.limitReachedMessage || window.cartStrings.error;
      }

      return '';
    });
  }

  function updateCartQuantityControls(cart) {
    var settings = getSettings();
    if (!settings.enabled || !cart) return;

    var priceCount = countPriceBasedQuantityInCart(cart, settings.minPriceCents);
    var blockIncrease = priceCount >= settings.maxLimit;

    document.querySelectorAll('quantity-input').forEach(function (quantityInput) {
      var input = quantityInput.querySelector('input.quantity__input');
      var plusButton = quantityInput.querySelector(".quantity__button[name='plus']");
      if (!input || !plusButton) return;

      var lineIndex = parseInt(input.dataset.index, 10);
      if (!lineIndex || !cart.items || !cart.items[lineIndex - 1]) return;

      var lineItem = cart.items[lineIndex - 1];
      if (!isPriceBasedCartItem(lineItem, settings.minPriceCents)) return;

      if (blockIncrease) {
        plusButton.setAttribute('disabled', 'disabled');
        plusButton.classList.add('disabled');
        plusButton.dataset.priceLimitPlusDisabled = 'true';
        return;
      }

      if (plusButton.dataset.priceLimitPlusDisabled === 'true') {
        delete plusButton.dataset.priceLimitPlusDisabled;
        if (!input.disabled) {
          plusButton.removeAttribute('disabled');
          plusButton.classList.remove('disabled');
        }
      }
    });
  }

  function updatePaidAddButtons(cart) {
    var settings = getSettings();
    if (!settings.enabled || !isProductPage()) return;

    var bar = getBar();
    var priceCount = countPriceBasedQuantityInCart(cart, settings.minPriceCents);
    var atLimit = priceCount >= settings.maxLimit;

    if (isPriceLimitBar(bar)) {
      if (atLimit) {
        showLimitMessage();
      } else {
        hideLimitMessage();
      }
    }

    document.querySelectorAll('form[data-type="add-to-cart-form"]:not(.sample-form)').forEach(function (form) {
      var submitButton = form.querySelector('[type="submit"]');
      if (!submitButton) return;

      var variantInput = form.querySelector('[name="id"]');
      var variantId = variantInput ? variantInput.value : null;
      var shouldDisable = atLimit && variantQualifiesForPriceLimit(variantId, settings);

      setButtonPriceLimitDisabled(submitButton, shouldDisable);
    });
  }

  function checkPriceBasedInCart() {
    var settings = getSettings();
    if (!settings.enabled) return Promise.resolve();

    return resolveCartSnapshot().then(function (cart) {
      var bar = getBar();
      if (bar) applyCartToPriceBar(bar, cart);
      updatePaidAddButtons(cart);
      updateCartQuantityControls(cart);
      return cart;
    });
  }

  function onCartUpdate(event) {
    var settings = getSettings();
    if (!settings.enabled) return;

    var bar = getBar();
    if (!bar || !isPriceLimitBar(bar)) return;

    if (event && event.cartData && event.cartData.items) {
      window.__priceBasedCartSnapshot = event.cartData;
      applyCartToPriceBar(bar, event.cartData);
      updatePaidAddButtons(event.cartData);
      updateCartQuantityControls(event.cartData);
      return;
    }

    window.__priceBasedCartSnapshot = null;
    refreshPriceBarFromCart(bar).then(function (cart) {
      if (cart) {
        window.__priceBasedCartSnapshot = cart;
        updatePaidAddButtons(cart);
        updateCartQuantityControls(cart);
      }
    });
  }

  function schedulePriceLimitCheck() {
    if (!getSettings().enabled) return;
    checkPriceBasedInCart();
  }

  function initCartQuantityListeners() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        window.__priceBasedCartSnapshot = null;
        if (event && event.cartData && event.cartData.items) {
          window.__priceBasedCartSnapshot = event.cartData;
          updateCartQuantityControls(event.cartData);
          if (isProductPage()) {
            updatePaidAddButtons(event.cartData);
          }
          return;
        }
        schedulePriceLimitCheck();
      });
    }

    document.addEventListener('cart:updated', function (event) {
      window.__priceBasedCartSnapshot = null;
      if (event.detail && event.detail.cart) {
        window.__priceBasedCartSnapshot = event.detail.cart;
        updateCartQuantityControls(event.detail.cart);
        if (isProductPage()) {
          updatePaidAddButtons(event.detail.cart);
        }
        return;
      }
      schedulePriceLimitCheck();
    });

    document.addEventListener(
      'click',
      function (event) {
        var plusButton = event.target.closest(".quantity__button[name='plus']");
        if (!plusButton || plusButton.dataset.priceLimitPlusDisabled !== 'true') return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );
  }

  function initProductPageListeners() {
    if (!isProductPage()) return;

    document.addEventListener('product-info:loaded', schedulePriceLimitCheck);
    window.addEventListener('load', schedulePriceLimitCheck);

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data ? event.data.variant : null;
        if (variant && variant.id != null && variant.price != null) {
          window.__productVariantPrices = window.__productVariantPrices || {};
          window.__productVariantPrices[String(variant.id)] = variant.price;
        }
        window.setTimeout(schedulePriceLimitCheck, 0);
      });

      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        window.__priceBasedCartSnapshot = null;
        if (event && event.cartData && event.cartData.items) {
          window.__priceBasedCartSnapshot = event.cartData;
          updatePaidAddButtons(event.cartData);
          updateCartQuantityControls(event.cartData);
          return;
        }
        schedulePriceLimitCheck();
      });
    }

    document.addEventListener('cart:updated', function (event) {
      window.__priceBasedCartSnapshot = null;
      if (event.detail && event.detail.cart) {
        window.__priceBasedCartSnapshot = event.detail.cart;
        updatePaidAddButtons(event.detail.cart);
        updateCartQuantityControls(event.detail.cart);
        return;
      }
      schedulePriceLimitCheck();
    });
  }

  function initBarListeners() {
    var settings = getSettings();
    if (!settings.enabled) return;

    var bar = getBar();
    if (!bar || !isPriceLimitBar(bar)) return;

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    }

    document.addEventListener('cart:updated', function (event) {
      if (event.detail && event.detail.cart) {
        window.__priceBasedCartSnapshot = event.detail.cart;
        applyCartToPriceBar(bar, event.detail.cart);
        updatePaidAddButtons(event.detail.cart);
        updateCartQuantityControls(event.detail.cart);
        return;
      }
      refreshPriceBarFromCart(bar);
    });

    refreshPriceBarFromCart(bar).then(function (cart) {
      if (cart) {
        window.__priceBasedCartSnapshot = cart;
        updatePaidAddButtons(cart);
        updateCartQuantityControls(cart);
      }
    });
  }

  function init() {
    if (!getSettings().enabled) return;

    initCartQuantityListeners();
    initProductPageListeners();

    var bar = getBar();
    if (isPriceLimitBar(bar)) {
      initBarListeners();
    }

    schedulePriceLimitCheck();
  }

  window.CartPriceLimit = {
    getSettings: getSettings,
    isPriceBasedCartItem: isPriceBasedCartItem,
    isPriceBasedVariantPrice: isPriceBasedVariantPrice,
    countPriceBasedQuantityInCart: countPriceBasedQuantityInCart,
    getVariantPrice: getVariantPrice,
    checkBeforeAdd: checkBeforeAdd,
    validateQuantityChange: validateQuantityChange,
    showLimitMessage: showLimitMessage,
    hideLimitMessage: hideLimitMessage,
    checkPriceBasedInCart: checkPriceBasedInCart,
    updatePaidAddButtons: updatePaidAddButtons,
    updateCartQuantityControls: updateCartQuantityControls,
    setButtonPriceLimitDisabled: setButtonPriceLimitDisabled,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
